import { Box, Stack } from "@chakra-ui/react";
import SearchInfiniteProvider from "@raiden/library/contexts/SearchInfinite/SearchInfiniteProvider";
import { useSearchInfinite } from "@raiden/library/contexts/SearchInfinite/useSearchInfinite";
import { useRouterArray } from "@raiden/library/hooks/router/useRouterArray";
import { useRouterString } from "@raiden/library/hooks/router/useRouterString";
import generateApiUrl from "@raiden/library/libraries/utils/generateApiUrl";
import { useCallback, useMemo } from "react";
import { Filters } from "./Filters";
import { Results } from "./Results";
import dayjs from "dayjs";
import { POSTS_STATE_VALUE_PUBLISHED } from "@raiden/library/constants/posts";

/**
 * @property {string} eventDate
 *
 * @return {{event_min_date: string, event_end_date: string}}
 */
function getEventDates({ eventDate }) {
  switch (eventDate) {
    case "today":
      return {
        event_min_date: dayjs().format("YYYY-MM-DD 00:00:00"),
        event_end_date: dayjs().format("YYYY-MM-DD 23:59:59"),
      };
    case "weekend":
      return {
        event_min_date: dayjs()
          .startOf("week")
          .add(5, "days")
          .format("YYYY-MM-DD 00:00:00"),
        event_end_date: dayjs()
          .startOf("week")
          .add(6, "days")
          .format("YYYY-MM-DD 23:59:59"),
      };
    case "week":
      return {
        event_min_date: dayjs().startOf("week").format("YYYY-MM-DD 00:00:00"),
        event_end_date: dayjs().endOf("week").format("YYYY-MM-DD 23:59:59"),
      };
    case "month":
      return {
        event_min_date: dayjs().startOf("month").format("YYYY-MM-DD 00:00:00"),
        event_end_date: dayjs().endOf("month").format("YYYY-MM-DD 23:59:59"),
      };
    case "year":
      return {
        event_min_date: dayjs().startOf("year").format("YYYY-MM-DD 00:00:00"),
        event_end_date: dayjs().endOf("year").format("YYYY-MM-DD 23:59:59"),
      };
    default:
      return {
        event_min_date: "",
        event_end_date: "",
      };
  }
}

/**
 * @typedef {object} PostsSearchFormValues
 * @property {string} term
 * @property {string[]} category_id
 * @property {string} event_date
 */

/** @type {PostsSearchFormValues} */
export const POST_SEARCH_FORM_DEFAULT_VALUES = {
  term: "",
  category_id: [],
  event_date: "",
};

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Post").PostType} postType
 * @property {import("swr").SWRResponse<import("@raiden/library/types/Api/ApiResponse").ApiResponse<import("@raiden/library/types/PostCategory").PostCategory[]>>} swrResponsePostCategories
 */
/**
 * @param {Props} props
 */
export function PostSearch({ postType, swrResponsePostCategories }) {
  const term = useRouterString("term");
  const categoryId = useRouterArray("category_id");
  const event_date = useRouterString("event_date");

  /** @type {import("@raiden/library/contexts/SearchInfinite/SearchInfiniteProvider").SearchInfiniteContextValue<PostsSearchFormValues>} */
  const searchInfiniteContext = useSearchInfinite({
    defaultValues: POST_SEARCH_FORM_DEFAULT_VALUES,
    initialFields: useMemo(
      () => ({
        term: term ?? "",
        category_id: categoryId,
        event_date: event_date ?? "",
      }),
      [categoryId, term, event_date],
    ),
    generateUrl: useCallback(
      (fields, key) =>
        generateApiUrl({
          id: "@api.posts.viewAny",
          query: {
            ...fields,
            ...getEventDates({ eventDate: fields.event_date }),
            page: key + 1,
            per_page: 9,
            type: [postType],
            state: [POSTS_STATE_VALUE_PUBLISHED],
            fields: [
              "post.current_revision",
              "post.categories",
              "post.revision.cover",
              "post.revision.files",
            ],
          },
        }),
      [postType],
    ),
    useRouterUrl: true,
    autoSubmit: true,
  });

  return (
    <SearchInfiniteProvider {...searchInfiniteContext}>
      <Stack spacing="1rem">
        <Box>
          <Filters
            swrResponsePostCategories={swrResponsePostCategories}
            postType={postType}
          />
        </Box>

        <Box flexGrow={1}>
          <Results />
        </Box>
      </Stack>
    </SearchInfiniteProvider>
  );
}
